import { activityLabel } from '@biopeak/health';
import {
  Card,
  DonutChart,
  type DonutSlice,
  Muted,
  RadialMeter,
  Stack,
  StackedBar,
  categorical,
  categoricalRest,
  color,
  space,
} from '@biopeak/ui';
import {
  usePatientSnapshot,
  type LastNight,
  type LastWeight,
  type PatientOverview,
  type StepsToday,
} from '../lib/patient.ts';
import { HemogramFindingsCard } from './HemogramFindingsCard.tsx';
import { PatientAnalysisCard } from './PatientAnalysisCard.tsx';
import { SerbioScoreCard } from './SerbioScoreCard.tsx';

/**
 * Cards fixos de visão geral, acima dos gráficos de tendência.
 *
 * Mesmo vocabulário visual do dashboard do paciente (gauge radial, barra
 * empilhada, número grande + legenda discreta) — o médico não deveria ter que
 * aprender duas gramáticas para ler o mesmo dado. O que muda é a densidade:
 * aqui cabem estatísticas do período, porque esta é ferramenta de trabalho e
 * não a tela de tranquilizar alguém sobre a própria saúde (§8.3).
 */
export function OverviewCards({
  data,
  patientId,
}: {
  data: PatientOverview;
  patientId: string;
}) {
  const snapshot = usePatientSnapshot(patientId);

  return (
    // No celular isto é um carrossel (`scroll-snap`, 1 card por vez, swipe
    // lateral) — no desktop (≥860px) volta a ser a grade de 3 colunas, ver
    // `.doctor-overview-grid` em `global.css`.
    <div className="doctor-overview-grid">
      <HeartRateCard hr={data.heart_rate} />
      <RespiratoryCard resp={data.respiratory} />
      <WeightCard weight={data.weight} last={data.weight_last} snapshot={snapshot.data?.last_weight} />
      <EnergyCard energy={data.active_energy} />
      <StepsCard steps={snapshot.data?.steps_today} />
      <SleepCard sleep={data.sleep} lastNight={snapshot.data?.last_night} />
      <Spo2Card spo2={data.spo2} />
      <HrvCard hrv={data.hrv} />
      <WorkoutsCard workouts={data.workouts} />
      <Vo2MaxCard vo2max={data.vo2max} last={data.vo2max_last} />
      <SerbioScoreCard patientId={patientId} />
      {/* No desktop ocupa a lacuna de tres cards ao lado do VO2 max
          (`doctor-overview-card--wide` = `grid-column: span 3`). No
          celular é só mais um slide do carrossel, largura cheia igual aos
          outros. */}
      <PatientAnalysisCard patientId={patientId} />
      <HemogramFindingsCard patientId={patientId} />
    </div>
  );
}

// ------------------------------------------------------------------ 1. FC

function HeartRateCard({ hr }: { hr: PatientOverview['heart_rate'] }) {
  if (!hr) return <EmptyCard title="Frequência cardíaca" />;

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Frequência cardíaca</CardTitle>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <RadialMeter
            value={hr.avg}
            min={hr.min}
            max={hr.max}
            size={150}
            valueLabel={String(hr.avg)}
            unitLabel="bpm médio"
            startEndpointLabel={String(hr.min)}
            endEndpointLabel={String(hr.max)}
          />
        </div>

        <StatRow>
          <Stat label="Mínima" value={`${hr.min}`} unit="bpm" />
          <Stat label="Máxima" value={`${hr.max}`} unit="bpm" />
          <Stat label="Variação" value={`${hr.cv}`} unit="%" />
        </StatRow>

        {/* A média do período é dominada por exercício — o relógio amostra
            muito mais durante treino. Sem esta linha o número grande do gauge
            seria lido como FC de repouso, que é outra coisa e é justamente a
            leitura clinicamente estável. */}
        <Muted style={{ fontSize: '12px' }}>
          Repouso {hr.resting ?? '—'} bpm · durante o sono {hr.nocturnal ?? '—'} bpm. A média acima
          é ponderada pelas {hr.samples.toLocaleString('pt-BR')} amostras do período, então pesa
          mais os momentos de exercício.
        </Muted>
      </Stack>
    </Card>
  );
}

// --------------------------------------------------- 2. FR (agregado diário)

/**
 * Frequência respiratória.
 *
 * O gauge destaca a FR NOTURNA quando ela existe. A primeira versão deste card
 * justificava isso dizendo que a média do dia inteiro "sobe com esforço e com
 * fala" — e o histórico do paciente de teste desmentiu: em 84 dias, 83% das
 * amostras JÁ eram do sono, e as duas médias diferem por 0,17 irpm. O Apple
 * Watch praticamente só mede FR dormindo.
 *
 * Então o card não anuncia contraste nenhum. Ele diz de onde veio o número —
 * qual a fatia de amostras noturnas — e só mostra a média do dia separada
 * quando ela de fato difere. Numa fonte que meça de dia (Android, cinta
 * torácica) a divergência aparece sozinha, e aí a linha se justifica.
 */
function RespiratoryCard({ resp }: { resp: PatientOverview['respiratory'] }) {
  if (!resp) return <EmptyCard title="Frequência respiratória" />;

  const destaque = resp.nocturnal ?? resp.avg;
  const rotulo = resp.nocturnal != null ? 'irpm no sono' : 'irpm médio';

  const fatiaNoturna = resp.samples > 0
    ? Math.round((resp.nocturnal_samples / resp.samples) * 100)
    : 0;

  // Meio irpm é o limiar de "vale a pena mencionar": abaixo disso a diferença
  // cabe no arredondamento e citá-la sugeriria um sinal que não existe.
  const divergem = resp.nocturnal != null && Math.abs(resp.avg - resp.nocturnal) >= 0.5;

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Frequência respiratória</CardTitle>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <RadialMeter
            value={destaque}
            min={resp.min}
            max={resp.max}
            size={150}
            valueLabel={String(destaque)}
            unitLabel={rotulo}
            startEndpointLabel={String(resp.min)}
            endEndpointLabel={String(resp.max)}
          />
        </div>

        <StatRow>
          <Stat label="Mínima" value={`${resp.min}`} unit="irpm" />
          <Stat label="Máxima" value={`${resp.max}`} unit="irpm" />
          <Stat label="Média" value={`${resp.avg}`} unit="irpm" />
        </StatRow>

        <Muted style={{ fontSize: '12px' }}>
          {resp.nocturnal != null
            ? `${fatiaNoturna}% das amostras vieram do sono. `
            : 'Nenhuma noite com sono registrado no período, então o destaque é a média do dia. '}
          {/* "do dia inteiro", não "fora do sono": `avg` inclui as amostras
              noturnas. Dizer "fora do sono" seria descrever um número que esta
              tabela não guarda. */}
          {divergem && `Considerando o dia inteiro, ${resp.avg} irpm. `}
          {resp.days} {resp.days === 1 ? 'dia' : 'dias'} agregados,{' '}
          {resp.samples.toLocaleString('pt-BR')} amostras.
        </Muted>
      </Stack>
    </Card>
  );
}

// --------------------------------------------------------------- 3. Peso

/**
 * Peso.
 *
 * A ÚLTIMA PESAGEM é o número principal — a mesma que o paciente vê no
 * celular. Antes o destaque era a média do período (66,2 kg contra os 65,5 kg
 * da tela dele), e a diferença não vinha de erro: vinha de as duas telas
 * responderem perguntas diferentes sem dizer qual.
 *
 * A média e a dispersão continuam no card. Em peso elas importam mais que em
 * outras métricas: uma balança de bioimpedância varia com hidratação, e a
 * pesagem isolada engana mais que a tendência.
 */
function WeightCard({
  weight,
  last,
  snapshot,
}: {
  weight: PatientOverview['weight'];
  last: PatientOverview['weight_last'];
  snapshot: LastWeight | null | undefined;
}) {
  // Peso é medido esporadicamente. Sumir com o card quando não há pesagem no
  // período esconderia a informação mais útil: quando foi a última.
  if (!weight) {
    return (
      <Card>
        <Stack gap={space.sm}>
          <CardTitle>Peso</CardTitle>
          {last ? (
            <>
              <BigNumber value={last.kg.toFixed(1)} unit="kg" />
              <Muted style={{ fontSize: '13px' }}>
                Nenhuma pesagem no período. Esta é a última registrada, em{' '}
                {new Date(last.at).toLocaleDateString('pt-BR')}.
              </Muted>
            </>
          ) : (
            <Muted style={{ fontSize: '13px' }}>Nenhuma pesagem registrada.</Muted>
          )}
        </Stack>
      </Card>
    );
  }

  const delta =
    snapshot && snapshot.previous_kg != null ? snapshot.kg - snapshot.previous_kg : null;

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Peso</CardTitle>

        {snapshot ? (
          <>
            <BigNumber
              value={snapshot.kg.toFixed(1)}
              unit={`kg em ${new Date(snapshot.at).toLocaleDateString('pt-BR')}`}
            />
            {delta != null && Math.abs(delta) >= 0.1 && (
              <Muted style={{ fontSize: '13px' }}>
                {delta > 0 ? '+' : ''}
                {delta.toFixed(1).replace('.', ',')} kg desde a pesagem anterior.
              </Muted>
            )}
          </>
        ) : (
          <BigNumber value={weight.avg.toFixed(1)} unit="kg em média" />
        )}

        {/* Período como contexto. Em peso a dispersão importa mais que em
            outras métricas: bioimpedância varia com hidratação, e a pesagem
            isolada engana mais que a tendência. */}
        <StatRow>
          <Stat label="Média do período" value={weight.avg.toFixed(1)} unit="kg" />
          <Stat label="Menor" value={weight.min.toFixed(1)} unit="kg" />
          <Stat label="Maior" value={weight.max.toFixed(1)} unit="kg" />
        </StatRow>
        <Muted style={{ fontSize: '12px' }}>
          {weight.count} {weight.count === 1 ? 'pesagem' : 'pesagens'} no período
          {/* Uma pesagem só não tem variação — mostrar "0%" afirmaria uma
              estabilidade que ninguém mediu. */}
          {weight.cv !== null && ` · variação de ${weight.cv}%`}
        </Muted>
      </Stack>
    </Card>
  );
}

// ------------------------------------------------------------- 8. VO2 máx

function Vo2MaxCard({
  vo2max,
  last,
}: {
  vo2max: PatientOverview['vo2max'];
  last: PatientOverview['vo2max_last'];
}) {
  // Mesmo problema do peso: o relógio estima VO2 máx esporadicamente (a cada
  // poucos dias), não todo dia. Sumir com o card num período sem leitura
  // esconderia justamente o dado mais útil: qual foi a última estimativa.
  if (!vo2max) {
    return (
      <Card>
        <Stack gap={space.sm}>
          <CardTitle>VO2 máx</CardTitle>
          {last ? (
            <>
              <BigNumber value={last.value.toFixed(1)} unit="mL/kg/min" />
              <Muted style={{ fontSize: '13px' }}>
                Nenhuma estimativa no período. Esta é a última registrada, em{' '}
                {new Date(last.at).toLocaleDateString('pt-BR')}.
              </Muted>
            </>
          ) : (
            <Muted style={{ fontSize: '13px' }}>Nenhuma estimativa registrada.</Muted>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>VO2 máx</CardTitle>
        <BigNumber value={vo2max.avg.toFixed(1)} unit="mL/kg/min em média" />
        <StatRow>
          <Stat label="Menor" value={vo2max.min.toFixed(1)} unit="mL/kg/min" />
          <Stat label="Maior" value={vo2max.max.toFixed(1)} unit="mL/kg/min" />
        </StatRow>
        <Muted style={{ fontSize: '12px' }}>
          {vo2max.count} {vo2max.count === 1 ? 'estimativa' : 'estimativas'} no período
        </Muted>
      </Stack>
    </Card>
  );
}

// ----------------------------------------------------------- 3. Calorias

function EnergyCard({ energy }: { energy: PatientOverview['active_energy'] }) {
  if (!energy) return <EmptyCard title="Calorias ativas" />;

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Calorias ativas</CardTitle>
        <BigNumber value={energy.avg_kcal.toLocaleString('pt-BR')} unit="kcal por dia" />
        <StatRow>
          <Stat label="Maior dia" value={energy.max_kcal.toLocaleString('pt-BR')} unit="kcal" />
          <Stat label="Total" value={energy.total_kcal.toLocaleString('pt-BR')} unit="kcal" />
        </StatRow>
        <Muted style={{ fontSize: '12px' }}>
          média de {energy.days} {energy.days === 1 ? 'dia' : 'dias'} com registro
        </Muted>
      </Stack>
    </Card>
  );
}

// --------------------------------------------------------------- 4. Sono

const SLEEP_STAGE = [
  { key: 'deep', label: 'Profundo', color: categorical[0] },
  { key: 'rem', label: 'REM', color: categorical[1] },
  { key: 'core', label: 'Leve', color: categorical[2] },
  { key: 'awake', label: 'Acordado', color: color.border },
] as const;

/**
 * Sono.
 *
 * A ÚLTIMA NOITE é o número principal, e a média do período fica como
 * contexto. A ordem importa e foi decidida por um problema real: o app do
 * paciente sempre mostrou a última noite, o painel mostrava só a média, e numa
 * paciente com uma noite partida ao meio isso virou 4h17 na tela dela contra
 * 3h14 na do médico. Nenhum dos dois errava a conta — respondiam perguntas
 * diferentes, e nada dizia qual. Num produto em que os dois conversam olhando
 * cada um a sua tela, isso é pior que um erro de cálculo.
 *
 * A última noite vem de `get_last_night`, que lê a MESMA view (`sleep_nightly`)
 * que alimenta o app do paciente. Não é "o mesmo cálculo refeito igual": é a
 * mesma linha do banco, o que torna impossível divergirem.
 *
 * `sleep.nights` conta só as noites com ESTÁGIO detalhado — são as únicas que
 * sustentam tempo dormido, eficiência e despertares. Uma sessão sem estágio
 * (achado real: 34 noites de um paciente com Zepp, por troca de relógio ou
 * mudança na origem) não desaparece: aparece como uma linha à parte, porque
 * `sleep_nightly` a exclui de propósito do resto do card.
 *
 * Essa linha diz "tempo na cama", nunca "dormiu" — sem estágio não há como
 * separar sono de vigília, e afirmar tempo dormido seria alegação clínica que
 * o dado não sustenta.
 */
function SleepCard({
  sleep,
  lastNight,
}: {
  sleep: PatientOverview['sleep'];
  lastNight: LastNight | null | undefined;
}) {
  if (!sleep && !lastNight) return <EmptyCard title="Sono" />;

  // Barra de estágios da ÚLTIMA noite quando ela existe; do período, quando
  // não. Misturar as duas seria mostrar a proporção de um período sob o
  // número de uma noite.
  const segments = lastNight
    ? SLEEP_STAGE.map((s) => ({
        key: s.key,
        label: s.label,
        value: lastNight[`${s.key}_s` as 'deep_s' | 'rem_s' | 'core_s' | 'awake_s'],
        color: s.color,
      })).filter((s) => s.value > 0)
    : SLEEP_STAGE.map((s) => ({
        key: s.key,
        label: s.label,
        value: sleep!.stages[s.key],
        color: s.color,
      })).filter((s) => s.value > 0);

  const semEstagio = sleep?.nights_without_stages ?? 0;
  const periodo = sleep && sleep.nights > 0 ? sleep : null;

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Sono</CardTitle>

        {lastNight && (
          <>
            <BigNumber value={formatHm(lastNight.total_s)} unit={`na noite de ${formatNight(lastNight.night)}`} />

            {segments.length > 0 && <StackedBar segments={segments} formatValue={formatHm} />}

            <StatRow>
              <Stat
                label="Eficiência"
                value={lastNight.efficiency == null ? '—' : `${lastNight.efficiency}`}
                unit="%"
              />
              <Stat label="Despertares" value={`${lastNight.awakenings}`} unit="na noite" />
              <Stat label="Na cama" value={formatHm(lastNight.in_bed_s)} unit="" />
            </StatRow>
          </>
        )}

        {/* Contexto do período. Rotulado de forma inequívoca, porque é
            justamente a confusão entre este número e o da noite que motivou
            a reformulação do card. */}
        {periodo && (
          <Stack
            gap={space.xs}
            style={lastNight ? { borderTop: `1px solid ${color.border}`, paddingTop: space.sm } : undefined}
          >
            {!lastNight && (
              <BigNumber value={formatHm(periodo.avg_total_s)} unit="por noite, em média" />
            )}
            <StatRow>
              <Stat label="Média do período" value={formatHm(periodo.avg_total_s)} unit="por noite" />
              <Stat label="Eficiência média" value={`${periodo.avg_efficiency}`} unit="%" />
              <Stat label="Noites" value={`${periodo.nights}`} unit="com dado" />
            </StatRow>
          </Stack>
        )}

        {sleep && semEstagio > 0 && (
          <Stack
            gap={space.xs}
            style={
              lastNight || periodo
                ? { borderTop: `1px solid ${color.border}`, paddingTop: space.sm }
                : undefined
            }
          >
            {/* Mesmo tratamento visual do número principal do card (28px) —
                média por noite é o que se lê de relance, não o total bruto. */}
            <BigNumber
              value={formatHm(sleep.total_s_without_stages / semEstagio)}
              unit="por noite, em média (na cama)"
            />
            <Muted style={{ fontSize: '12px' }}>
              {semEstagio} {semEstagio === 1 ? 'noite registrada' : 'noites registradas'} sem
              detalhe de estágio, totalizando {formatHm(sleep.total_s_without_stages)} na cama.
              Sem separação entre sono e vigília — o relógio não enviou essa informação.
            </Muted>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

// -------------------------------------------------------- 5. Saturação

function Spo2Card({ spo2 }: { spo2: PatientOverview['spo2'] }) {
  if (!spo2) return <EmptyCard title="Saturação de oxigênio" />;

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Saturação de oxigênio</CardTitle>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <RadialMeter
            value={spo2.avg}
            // Escala 88–100: abaixo de 88 a leitura de wearable já não é
            // confiável, e começar em 0 achataria toda a faixa útil num canto
            // do arco.
            min={88}
            max={100}
            size={150}
            valueLabel={String(spo2.avg)}
            unitLabel="% em média"
            startEndpointLabel="88"
            endEndpointLabel="100"
          />
        </div>

        <StatRow>
          <Stat label="Menor" value={`${spo2.min}`} unit="%" />
          <Stat label="Medições" value={`${spo2.count}`} unit="" />
          <Stat label="Abaixo de 95%" value={`${spo2.below_95}`} unit="" />
        </StatRow>

        {/* 95% é referência descritiva. Quem decide o que vira alerta é a
            regra que o médico configura por paciente (§4.3) — este card
            nunca opina sobre isso. */}
        <Muted style={{ fontSize: '12px' }}>
          A contagem abaixo de 95% é descritiva, não é limiar de alerta.
        </Muted>
      </Stack>
    </Card>
  );
}

// ------------------------------------------------------------- 6. VFC

const HRV_LABEL: Record<string, string> = {
  sdnn: 'SDNN',
  rmssd: 'RMSSD',
};

function HrvCard({ hrv }: { hrv: PatientOverview['hrv'] }) {
  const variants = ['sdnn', 'rmssd'].filter((v) => hrv[v]);

  if (variants.length === 0) return <EmptyCard title="Variabilidade da FC" />;

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Variabilidade da FC</CardTitle>

        {variants.map((v) => {
          const m = hrv[v]!;
          return (
            <Stack key={v} gap={space.xs}>
              <BigNumber value={String(m.avg)} unit={`ms — ${HRV_LABEL[v]}`} />
              <Muted style={{ fontSize: '12px' }}>
                entre {m.min} e {m.max} ms, {m.count}{' '}
                {m.count === 1 ? 'medição' : 'medições'}
              </Muted>
            </Stack>
          );
        })}

        {/* Dizer qual falta é diferente de omitir: o médico precisa saber que
            a ausência é da fonte, não do paciente. */}
        {variants.length === 1 && (
          <Muted style={{ fontSize: '12px' }}>
            {HRV_LABEL[variants[0] === 'sdnn' ? 'rmssd' : 'sdnn']} não é enviado pelo aparelho
            deste paciente.
          </Muted>
        )}
      </Stack>
    </Card>
  );
}

// -------------------------------------------------------- 7. Exercícios

/** Acima de quatro tipos a rosca deixa de ser leitura e vira decoração — e a
 *  paleta validada para daltonismo só tem quatro cores distinguíveis mais o
 *  cinza de "Outros". Os dois limites coincidem, então o corte é aqui. */
const MAX_SLICES = 4;

function WorkoutsCard({ workouts }: { workouts: PatientOverview['workouts'] }) {
  if (workouts.count === 0) return <EmptyCard title="Exercícios físicos" />;

  const top = workouts.by_type.slice(0, MAX_SLICES);
  const rest = workouts.by_type.slice(MAX_SLICES);

  // Anotado como DonutSlice[]: `categorical` é `as const`, então sem a
  // anotação o TS infere a união dos quatro literais e recusa o cinza de
  // "Outros" no push logo abaixo.
  const slices: DonutSlice[] = top.map((t, i) => ({
    key: t.type,
    label: activityLabel(t.type),
    value: t.count,
    // `?? categoricalRest` só existe para o `noUncheckedIndexedAccess`:
    // `top` tem no máximo MAX_SLICES itens e MAX_SLICES é o tamanho da
    // paleta, então o índice nunca falta de verdade.
    color: categorical[i] ?? categoricalRest,
  }));

  if (rest.length > 0) {
    slices.push({
      key: '__outros',
      label: `Outros (${rest.length})`,
      value: rest.reduce((sum, t) => sum + t.count, 0),
      color: categoricalRest,
    });
  }

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Exercícios físicos</CardTitle>

        <DonutChart
          slices={slices}
          centerValue={String(workouts.count)}
          centerLabel={workouts.count === 1 ? 'sessão' : 'sessões'}
        />

        <StatRow>
          <Stat label="Tempo total" value={formatHm(workouts.duration_s)} unit="" />
          <Stat label="Gasto" value={workouts.kcal.toLocaleString('pt-BR')} unit="kcal" />
        </StatRow>
      </Stack>
    </Card>
  );
}

// ------------------------------------------------------------ auxiliares

function CardTitle({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: '15px', color: color.textSecondary }}>{children}</span>;
}

function BigNumber({ value, unit }: { value: string; unit: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: space.xs }}>
      <span style={{ fontSize: '28px', fontWeight: 600, letterSpacing: '-0.015em' }}>{value}</span>
      <Muted style={{ fontSize: '14px' }}>{unit}</Muted>
    </div>
  );
}

function StatRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: space.lg,
        flexWrap: 'wrap',
        borderTop: `1px solid ${color.border}`,
        paddingTop: space.sm,
      }}
    >
      {children}
    </div>
  );
}

function Stat({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <Stack gap="2px">
      <span style={{ fontSize: '17px', fontWeight: 600 }}>
        {value}
        {unit && <span style={{ fontSize: '12px', fontWeight: 400 }}> {unit}</span>}
      </span>
      <Muted style={{ fontSize: '12px' }}>{label}</Muted>
    </Stack>
  );
}

function EmptyCard({ title }: { title: string }) {
  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>{title}</CardTitle>
        <Muted style={{ fontSize: '13px' }}>Sem dado no período.</Muted>
      </Stack>
    </Card>
  );
}

/** "7h20" ou "42min" — o mesmo formato do app do paciente. */
function formatHm(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h === 0) return `${m}min`;
  return `${h}h${m.toString().padStart(2, '0')}`;
}

/** "14/08" — curto, porque divide espaço com o número grande do card. */
function formatNight(iso: string): string {
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
}

// ----------------------------------------------------------- Passos hoje

/**
 * Passos de hoje — a métrica que o médico não via.
 *
 * O app do paciente sempre teve card de passos; o painel não tinha nenhum, em
 * campo nenhum. Não era divergência de número, era ausência: o médico não
 * conseguia conversar sobre atividade olhando o mesmo dado que o paciente.
 *
 * SÓ HOJE, como no app. Cair para "o último dia com registro" faria o painel
 * exibir os passos de anteontem sob o rótulo "hoje" — a mesma classe de erro
 * que esta leva de correções existe para eliminar.
 */
function StepsCard({ steps }: { steps: StepsToday | null | undefined }) {
  if (!steps) {
    return (
      <Card>
        <Stack gap={space.sm}>
          <CardTitle>Passos hoje</CardTitle>
          <Muted style={{ fontSize: '13px' }}>
            Nenhum registro de hoje ainda. O relógio envia ao longo do dia, e o
            número aparece aqui igual ao que o paciente vê no celular.
          </Muted>
        </Stack>
      </Card>
    );
  }

  const km = steps.distance_m != null ? steps.distance_m / 1000 : null;

  return (
    <Card>
      <Stack gap={space.sm}>
        <CardTitle>Passos hoje</CardTitle>
        <BigNumber value={steps.steps.toLocaleString('pt-BR')} unit="passos" />
        {km != null && (
          <Muted style={{ fontSize: '13px' }}>
            {km.toFixed(1).replace('.', ',')} km percorridos.
          </Muted>
        )}
      </Stack>
    </Card>
  );
}
